import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import times from 'lodash/times';
import styles from './ResponsiveGridLayout.scss';

const GUTTER_SIZES = {
  'x-large': 32,
  large: 24,
  medium: 16,
  small: 8,
  'x-small': 4,
};

/**
 * The list of values here should be kept in sync with the list
 * $min-column-widths in ResponsiveGridLayout.scss to ensure that
 * breakpoint-specific CSS is generatd for the relevant minimum column widths.
 */
const MIN_COLUMN_WIDTHS = [160, 260, 400];

/**
 * Likewise the list of maximum column counts must also be updated in
 * ResponsiveGridLayout.scss if it’s changed here.
 */
const MAX_COLUMN_COUNTS = [2, 3, 4, 5, 6];

/**
 * A layout container for displaying items in responsive grid columns.
 */
export default class ResponsiveGridLayout extends React.Component {
  state = {
    hasNoGridSupport: undefined,
    columnCount: undefined,
    cellIndexes: [],
  };

  /**
   * Feature-detecting support for display: grid is necessary since otherwise
   * we’ll end up with inline style properties being applied to children which
   * would clash with those used by browsers that *do* support display: grid.
   */
  componentDidMount() {
    const hasNoGridSupport = !(
      Object.hasOwnProperty.call(CSS, 'supports') && CSS.supports('display: grid')
    );
    this.setState({ hasNoGridSupport });

    if (hasNoGridSupport) {
      this.setColumnCount();
      this.updateCellIndexes();
      window.addEventListener('resize', this.setColumnCount);
    }
  }

  componentWillUnmount() {
    const { hasNoGridSupport } = this.state;

    if (hasNoGridSupport) {
      window.removeEventListener('resize', this.setColumnCount);
    }
  }

  /**
   * If the children within this element change, we need to update the cell
   * indexes to ensure that any full-width rows correctly increment the cell
   * index of subsequent cells (for things like horizontal margins).
   */
  componentDidUpdate(prevProps) {
    const { children } = this.props;
    const { hasNoGridSupport } = this.state;

    if (hasNoGridSupport && children !== prevProps.children) {
      this.updateCellIndexes();
    }
  }

  updateCellIndexes() {
    const { children } = this.props;
    const { columnCount } = this.state;
    let cellOffset = 0;

    const cellIndexes = React.Children.map(children, (child, childIndex) => {
      const { displayName } = child.type;
      const cellIndex = childIndex + cellOffset;

      /**
       * If we introduce a ResponsiveGridRow, we need to increment the offset
       * for subsequent cells by the width of that row, i.e. the number of
       * columns.
       */
      if (displayName === 'ResponsiveGridRow') {
        /**
         * also, if introducing a full-width row here would introduce orphans,
         * we need to also increment the offset by columnCount - number of
         * orphans.
         */
        if (cellIndex % columnCount !== 0) {
          cellOffset += columnCount - (cellIndex % columnCount);
        }

        cellOffset += columnCount - 1;
      }

      return cellIndex;
    });

    this.setState({ cellIndexes });
  }

  getGutterWidth() {
    const { gutter } = this.props;
    return GUTTER_SIZES[gutter];
  }

  getMinWidth(columnCount) {
    const { minColumnWidth } = this.props;
    return columnCount * minColumnWidth + this.getGutterWidth() * (columnCount - 1);
  }

  calculateColumns(elementWidth) {
    const { maxColumns } = this.props;
    let columnCount = 1;

    while (this.getMinWidth(columnCount + 1) < elementWidth && columnCount < maxColumns) {
      columnCount++;
    }

    return columnCount;
  }

  setColumnCount = () => {
    if (this.element) {
      const columnCount = this.calculateColumns(this.element.offsetWidth);
      this.setState({ columnCount }, this.updateCellIndexes);
    }
  };

  getFallbackStyles(index) {
    const { minColumnWidth } = this.props;
    const { columnCount, cellIndexes } = this.state;

    const columnWidthPercent = 100 / columnCount;
    const cellIndex = cellIndexes[index];
    const columnWidthCalc = `calc(${columnWidthPercent}% - ${(this.getGutterWidth() *
      (columnCount - 1)) /
      columnCount}px)`;

    let style = {
      flex: `1 1 ${columnWidthCalc}`,
      minWidth: `${minColumnWidth}px`,
      maxWidth: columnCount > 1 ? columnWidthCalc : undefined,
    };

    if (cellIndex > columnCount - 1) {
      style = {
        ...style,
        marginTop: `${this.getGutterWidth()}px`,
      };
    }

    if (cellIndex % columnCount !== 0) {
      style = {
        ...style,
        marginLeft: `${this.getGutterWidth()}px`,
      };
    }

    return style;
  }

  /**
   * This whole method is only necessary to support browsers that don’t support
   * display: grid (i.e. IE11). Assuming we no longer need to support that
   * browser, the entire method can be replaced in favour of just returning
   * {children} in the main render() method. In fact, the component could be
   * made into a pure functional component, since local state would be
   * redundant.
   */
  renderChildren() {
    const { children, gutter } = this.props;
    const { hasNoGridSupport } = this.state;

    if (!hasNoGridSupport) return children;

    return React.Children.map(children, (child, index) => {
      const { displayName } = child.type;
      switch (displayName) {
        /**
         * The ResponsiveGridRow component has native fallback CSS within its
         * own styles.
         */
        case 'ResponsiveGridRow':
          return React.cloneElement(child, { gutter, hasNoGridSupport: true });
        default:
          return <div style={this.getFallbackStyles(index)}>{React.cloneElement(child)}</div>;
      }
    });
  }

  render() {
    const { minColumnWidth, maxColumns, gutter, hasMobileGutter } = this.props;
    const { hasNoGridSupport } = this.state;
    let gridClassNames = [styles.grid];
    if (!hasMobileGutter) gridClassNames.push(styles['no-gutter']);

    if (!hasNoGridSupport) {
      gridClassNames = [
        ...gridClassNames,
        styles[`gutter-${gutter}`],
        styles[`min-width-${minColumnWidth}px`],
      ];

      times(maxColumns, (i) => {
        if (i > 0) gridClassNames.push(styles[`max-columns-${i + 1}`]);
      });
    }

    return (
      <div
        ref={(element) => (this.element = element)} // eslint-disable-line no-return-assign
        className={classnames(gridClassNames)}
      >
        {this.renderChildren()}
      </div>
    );
  }
}

ResponsiveGridLayout.propTypes = {
  /**
   * List of child nodes, e.g. cards.
   */
  children: PropTypes.node.isRequired,

  /**
   * Maximum number of grid columns.
   */
  maxColumns: PropTypes.oneOf(MAX_COLUMN_COUNTS),

  /**
   * Minimum width in screen points columns should render at before breaking
   * down to one less column.
   */
  minColumnWidth: PropTypes.oneOf(MIN_COLUMN_WIDTHS).isRequired,

  /**
   * Gutter size between rows and columns.
   */
  gutter: PropTypes.oneOf(Object.keys(GUTTER_SIZES)),

  /**
   * Should the gutter exist at mobile breakpoints?
   */
  hasMobileGutter: PropTypes.bool,
};

ResponsiveGridLayout.defaultProps = {
  maxColumns: 4,
  gutter: 'medium',
  hasMobileGutter: true,
};
